import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Library, Save, Trash2, Upload, Download, FileText, Plus, Sparkles, FileUp } from "lucide-react";
import { CatalogApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { SkillBuilder } from "../components/SkillBuilder";
import { formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import type { SkillCatalogItem } from "../lib/types";

export default function Catalog() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);

  const { data: items = [], isLoading, error } = useQuery({ queryKey: qk.skills.catalog, queryFn: CatalogApi.list });

  const deleteMut = useMutation({
    mutationFn: CatalogApi.deleteSkill,
    onSuccess: () => {
      toast.success("Skill deleted");
      setSelected(null);
      qc.invalidateQueries({ queryKey: qk.skills.catalog });
    },
  });

  // Import a NEW skill from a .skill/.zip package or a SKILL.md.
  const fileRef = useRef<HTMLInputElement>(null);
  const importMut = useMutation({
    mutationFn: (file: File) => CatalogApi.importSkill(file),
    onSuccess: (r) => {
      toast.success(`Imported skill "${r.name}"`, `${r.files} file(s) — assign it from Skills when ready.`);
      qc.invalidateQueries({ queryKey: qk.skills.catalog });
      setSelected(r.name);
    },
    onError: (err) => {
      const msg = String(err);
      if (msg.includes("already exists")) toast.error("Skill already exists", "Delete the existing one first, or rename the package's skill.");
      else toast.error("Import failed", msg);
    },
  });

  return (
    <div className="h-full flex flex-col min-h-0">
      <input
        ref={fileRef}
        type="file"
        accept=".skill,.zip,.md"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) importMut.mutate(f);
          e.target.value = "";
        }}
      />
      <PageHeader
        title="Catalog"
        icon={Library}
        subtitle={`${items.length} skills in the canonical store`}
        actions={
          <div className="relative">
            <Button size="sm" onClick={() => setShowMenu((v) => !v)} loading={importMut.isPending}>
              <Plus className="size-3.5" />
              New skill
            </Button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 mt-1 z-20 w-56 rounded-md border border-border bg-surface-elevated shadow-lg py-1">
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-surface-2 flex items-center gap-2"
                    onClick={() => { setShowMenu(false); setShowBuilder(true); }}
                  >
                    <Sparkles className="size-3.5 text-accent" />
                    <span className="text-sm text-fg">Create with agent</span>
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-surface-2 flex items-center gap-2"
                    onClick={() => { setShowMenu(false); fileRef.current?.click(); }}
                  >
                    <FileUp className="size-3.5 text-fg-muted" />
                    <span className="text-sm text-fg">Import file…</span>
                  </button>
                </div>
              </>
            )}
          </div>
        }
      />
      {showBuilder && (
        <SkillBuilder
          onClose={() => setShowBuilder(false)}
          onImported={(name) => { qc.invalidateQueries({ queryKey: qk.skills.catalog }); setSelected(name); }}
        />
      )}
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<SkillCatalogItem>
          items={items}
          itemKey={(s) => s.name}
          selectedId={selected}
          onSelect={setSelected}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={64}
          emptyState={<EmptyState icon={<Library className="size-8" />} title="No skills" />}
          renderItem={({ item }) => <CatalogRow s={item} />}
          renderDetail={(s) => (
            <CatalogDetail
              meta={s}
              onDelete={() => {
                if (confirm(`Delete skill "${s.name}"?`)) deleteMut.mutate(s.name);
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

function CatalogRow({ s }: { s: SkillCatalogItem }) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium text-fg font-mono truncate">{s.name}</span>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono">{s.file_count}f · {formatBytes(s.size)}</span>
      </div>
      <div className="text-xs text-fg-muted line-clamp-1">{s.description || "(no description)"}</div>
    </div>
  );
}

function CatalogDetail({ meta, onDelete }: { meta: SkillCatalogItem; onDelete: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: qk.skills.detail(meta.name), queryFn: () => CatalogApi.detail(meta.name) });
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const fileInputRef = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    if (data && data.files.length > 0 && !selectedFile) {
      setSelectedFile(data.files[0].path);
      setContent(data.files[0].content);
      setOriginal(data.files[0].content);
    }
  }, [data, selectedFile]);

  useEffect(() => {
    if (!data) return;
    const f = data.files.find((x) => x.path === selectedFile);
    if (f) {
      setContent(f.content);
      setOriginal(f.content);
    }
  }, [selectedFile, data]);

  const saveMut = useMutation({
    mutationFn: () => CatalogApi.saveFile(meta.name, selectedFile!, content),
    onSuccess: () => {
      setOriginal(content);
      qc.invalidateQueries({ queryKey: qk.skills.detail(meta.name) });
      qc.invalidateQueries({ queryKey: qk.skills.catalog });
      toast.success("File saved");
    },
    onError: (err) => toast.error("Save failed", String(err)),
  });
  const uploadMut = useMutation({
    mutationFn: (file: File) => CatalogApi.uploadFile(meta.name, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.skills.detail(meta.name) });
      qc.invalidateQueries({ queryKey: qk.skills.catalog });
      toast.success("Uploaded");
    },
    onError: (err) => toast.error("Upload failed", String(err)),
  });

  // Clean replace from a .zip/.skill package (drops orphaned files; keeps .env).
  const replaceMut = useMutation({
    mutationFn: (file: File) => CatalogApi.replaceSkill(meta.name, file),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: qk.skills.detail(meta.name) });
      qc.invalidateQueries({ queryKey: qk.skills.catalog });
      toast.success("Skill replaced", `${r.files} file(s)${r.kept_env ? " · kept existing .env" : ""}`);
    },
    onError: (err) => toast.error("Replace failed", String(err)),
  });

  if (isLoading || !data) return <CenteredSpinner />;

  const dirty = content !== original;

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-fg font-mono">{meta.name}</h2>
            <Pill>{meta.file_count} files</Pill>
            <Pill>{formatBytes(meta.size)}</Pill>
          </div>
          <p className="text-xs text-fg-muted mt-1 max-w-prose">{meta.description}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <a
            href={CatalogApi.downloadUrl(meta.name)}
            download={`${meta.name}.skill`}
            className="inline-flex items-center justify-center h-7 px-2.5 rounded text-xs gap-1.5 bg-surface-elevated border border-border text-fg cursor-pointer hover:bg-surface-2"
            title="Download the whole skill as a .skill package, exactly as it is on disk"
          >
            <Download className="size-3.5" />
            Download
          </a>
          <label
            className="inline-flex items-center justify-center h-7 px-2.5 rounded text-xs gap-1.5 bg-surface-elevated border border-border text-fg cursor-pointer hover:bg-surface-2"
            title="Add/overwrite individual files (additive — leaves other files untouched)"
          >
            <Upload className="size-3.5" />
            Upload
            <input
              type="file"
              ref={(el) => { fileInputRef[0] = el; }}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadMut.mutate(f);
                e.target.value = "";
              }}
            />
          </label>
          <label
            className={cn(
              "inline-flex items-center justify-center h-7 px-2.5 rounded text-xs gap-1.5 bg-surface-elevated border border-border text-fg cursor-pointer hover:bg-surface-2",
              replaceMut.isPending && "opacity-50 pointer-events-none",
            )}
            title="Replace the whole skill from a .zip/.skill package — files not in it are removed (.env kept)"
          >
            <FileUp className="size-3.5" />
            Replace
            <input
              type="file"
              accept=".zip,.skill"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && confirm(
                  `Replace skill "${meta.name}" with "${f.name}"?\n\n` +
                  `This installs the package as the skill's complete contents — any current files NOT in the package are removed. A committed .env is kept if the package doesn't include one. This cannot be undone.`,
                )) replaceMut.mutate(f);
                e.target.value = "";
              }}
            />
          </label>
          <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete"><Trash2 className="size-3.5" /></Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* File tree */}
        <div className="w-56 flex-shrink-0 border-r border-border overflow-y-auto">
          {data.files.length === 0 ? (
            <div className="p-4 text-xs text-fg-muted">No files</div>
          ) : (
            <ul className="py-1">
              {data.files.map((f) => (
                <li key={f.path}>
                  <button
                    onClick={() => setSelectedFile(f.path)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-surface-2 font-mono",
                      selectedFile === f.path ? "bg-surface-2 text-fg" : "text-fg-muted",
                    )}
                  >
                    <FileText className="size-3 flex-shrink-0 text-fg-subtle" />
                    <span className="truncate">{f.path}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 min-w-0 flex flex-col">
          {selectedFile ? (
            <>
              <div className="px-3 py-2 border-b border-border text-xs text-fg-muted font-mono flex items-center justify-between">
                <span>{selectedFile}</span>
                {dirty && <span className="text-warning">unsaved</span>}
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="flex-1 w-full font-mono text-xs rounded-none border-0 focus:ring-0 resize-none"
                spellCheck={false}
              />
              <div className="px-3 py-2 border-t border-border flex justify-end">
                <Button size="sm" onClick={() => saveMut.mutate()} disabled={!dirty} loading={saveMut.isPending}>
                  <Save className="size-3.5" />
                  Save file
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-fg-muted">Select a file to edit</div>
          )}
        </div>
      </div>
    </div>
  );
}
